import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", sort_order: 0 });
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/admin/categories"), api.get("/admin/products")]);
    setCats(c.data);
    const map = {};
    p.data.forEach((pr) => (map[pr.category_id] = (map[pr.category_id] || 0) + 1));
    setCounts(map);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", description: "", sort_order: cats.length }); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, description: c.description, sort_order: c.sort_order }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    try {
      if (editId) await api.put(`/admin/categories/${editId}`, form);
      else await api.post("/admin/categories", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Categories</h1>
          <p className="mt-1 text-sm text-muted-foreground">{cats.length} categories</p>
        </div>
        <Button onClick={openNew} data-testid="add-category-btn" className="rounded-lg bg-primary font-semibold text-primary-foreground hover:bg-[#7CBCE0]"><Plus className="mr-1.5 h-4 w-4" /> Add Category</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="overline">Name</TableHead>
            <TableHead className="overline">Description</TableHead>
            <TableHead className="overline">Products</TableHead>
            <TableHead className="overline text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {cats.map((c) => (
              <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`category-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-muted-foreground">{c.description || "—"}</TableCell>
                <TableCell>{counts[c.id] || 0}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(c)} data-testid={`delete-category-${c.id}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="category-description" rows={2} /></div>
            <div className="space-y-2"><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} data-testid="category-sort" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-category-btn" className="bg-primary text-primary-foreground hover:bg-[#7CBCE0]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
